import type { GeoJsonObject } from 'geojson';
import type { AssessmentBlueprint as SharedAssessmentBlueprint } from '@/api/network/types';
import type {
  RiskHistoryEntry,
  RiskPrioritizationResponse,
  RiskScores,
} from '@/api/risk/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AssessmentBlueprint,
  AssessmentBlueprintDetail,
  AssessmentBlueprintsResponse,
  AssessmentHistoryResponse,
  AssessmentMitigationsResponse,
  AssessmentNotesResponse,
  AssessmentRiskFactorsResponse,
  AssessmentRiskPrioritizationResponse,
  BlueprintCompaniesResponse,
  BlueprintPrioritizationResponse,
  CompanyAssessment,
  CountryRiskDetail,
  CreateAssessmentBlueprintPayload,
  CreateAssessmentNotePayload,
  CreateManualMitigationPayload,
  CreateRiskRulePayload,
  CreateRiskRuleResponse,
  MatchedRulesResponse,
  RecalculateAssessmentsPayload,
  RiskItemsResponse,
  RiskLandscapeCompaniesResponse,
  RiskLandscapeCountriesResponse,
  RiskLandscapeMapResponse,
  RiskLandscapeResponse,
  RiskRulesResponse,
  RuleTemplate,
  UpdateAssessmentBlueprintPayload,
  UpdateRuleTemplatePayload,
} from './types';

export async function fetchCompanyAssessmentBlueprints(
  companyId: string,
  qs: string,
): Promise<{
  data: SharedAssessmentBlueprint[];
  meta: { totalCount: number };
}> {
  return apiFetch(
    `/api/risk-analysis/assessment-blueprints/${companyId}/assessments?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchRiskScores(
  companyId: string,
  blueprintId: string,
): Promise<RiskScores> {
  const res = await apiFetch<{ data: RiskScores }>(
    `/api/risk-analysis/risk-profiles/${companyId}/blueprints/${blueprintId}/risk-scores`,
    {},
    API_ROOT_URL,
  );
  return res.data;
}

export async function fetchRiskPrioritizationDetails(
  companyId: string,
  blueprintId: string,
  qs: string,
): Promise<RiskPrioritizationResponse> {
  return apiFetch(
    `/api/risk-analysis/risk-profiles/${companyId}/blueprints/${blueprintId}/prioritization-details?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchRiskHistory(
  companyId: string,
  blueprintId: string,
): Promise<RiskHistoryEntry[]> {
  const res = await apiFetch<{ data: RiskHistoryEntry[] }>(
    `/api/risk-analysis/assessment-blueprints/${blueprintId}/company/${companyId}/history`,
    {},
    API_ROOT_URL,
  );
  return res.data;
}

export async function recalculateRiskAssessment(
  blueprintId: string,
  companyId: string,
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/assessments/calculate',
    {
      method: 'POST',
      body: JSON.stringify({ blueprintId, companyIds: [companyId] }),
    },
    API_ROOT_URL,
  );
}

export async function fetchGlobalRiskLandscape(
  qs: string,
): Promise<RiskLandscapeResponse> {
  return apiFetch<RiskLandscapeResponse>(
    `/api/risk-analysis/risk-landscape?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchRiskLandscapeMap(
  qs: string,
): Promise<RiskLandscapeMapResponse> {
  return apiFetch<RiskLandscapeMapResponse>(
    `/api/risk-analysis/risk-landscape/map?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchCountryRiskDetail(
  countryCode: string,
): Promise<CountryRiskDetail> {
  const res = await apiFetch<{ data: CountryRiskDetail }>(
    `/api/risk-analysis/risk-landscape/${countryCode}`,
    {},
    API_ROOT_URL,
  );
  return res.data;
}

export async function fetchRiskLandscapeCountries(
  qs: string,
): Promise<RiskLandscapeCountriesResponse> {
  return apiFetch<RiskLandscapeCountriesResponse>(
    `/api/risk-analysis/risk-landscape/countries?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchRiskLandscapeCompanies(
  qs: string,
): Promise<RiskLandscapeCompaniesResponse> {
  return apiFetch<RiskLandscapeCompaniesResponse>(
    `/api/risk-analysis/risk-landscape/companies?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAllRisks(): Promise<RiskItemsResponse> {
  return apiFetch<RiskItemsResponse>(
    '/api/risk-analysis/risks?page=0&count=300',
    {},
    API_ROOT_URL,
  );
}

// ─── Risk Rules ──────────────────────────────────────────────────────────────

export async function fetchRiskRules(qs: string): Promise<RiskRulesResponse> {
  return apiFetch<RiskRulesResponse>(
    `/api/risk-analysis/rule-templates?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function createRiskRule(
  payload: CreateRiskRulePayload,
): Promise<CreateRiskRuleResponse> {
  return apiFetch<CreateRiskRuleResponse>(
    '/api/risk-analysis/rule-templates',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

// ─── Trigger builder option APIs (match old frontend endpoints exactly) ────────

export async function fetchRiskDataPoints(): Promise<{
  data: { id: string; name: string }[];
}> {
  return apiFetch('/api/risk-analysis/datapoints', {}, API_ROOT_URL);
}

export async function fetchRiskAuditTypes(
  qs = '',
): Promise<{ data: { id: string; name: string }[] }> {
  const base = 'count=500';
  return apiFetch(
    `/api/risk-analysis/audit-types?${qs ? `${qs}&${base}` : base}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchRiskCertifications(): Promise<{
  data: { id: string; name: string }[];
}> {
  return apiFetch(
    '/api/risk-analysis/certifications?count=500',
    {},
    API_ROOT_URL,
  );
}

export async function fetchRiskImpactAreas(): Promise<{
  data: { id: string; description: string }[];
}> {
  return apiFetch(
    '/api/risk-analysis/impact-areas?count=500',
    {},
    API_ROOT_URL,
  );
}

export async function fetchRuleTemplate(id: string): Promise<RuleTemplate> {
  const res = await apiFetch<{ data: RuleTemplate } | RuleTemplate>(
    `/api/risk-analysis/rule-templates/${id}`,
    {},
    API_ROOT_URL,
  );
  return 'data' in res &&
    res.data &&
    typeof res.data === 'object' &&
    'id' in res.data
    ? (res as { data: RuleTemplate }).data
    : (res as RuleTemplate);
}

export async function updateRuleTemplate(
  id: string,
  payload: UpdateRuleTemplatePayload,
): Promise<RuleTemplate> {
  const res = await apiFetch<{ data: RuleTemplate } | RuleTemplate>(
    `/api/risk-analysis/rule-templates/${id}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
  return 'data' in res &&
    res.data &&
    typeof res.data === 'object' &&
    'id' in res.data
    ? (res as { data: RuleTemplate }).data
    : (res as RuleTemplate);
}

export async function bulkCloneRiskRules(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/rule-templates/copy',
    { method: 'POST', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}

// ─── Assessment Blueprints ────────────────────────────────────────────────────

export async function fetchAssessmentBlueprints(
  qs: string,
): Promise<AssessmentBlueprintsResponse> {
  return apiFetch<AssessmentBlueprintsResponse>(
    `/api/risk-analysis/assessment-blueprints?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function createAssessmentBlueprint(
  payload: CreateAssessmentBlueprintPayload,
): Promise<AssessmentBlueprint> {
  const res = await apiFetch<{ data: AssessmentBlueprint }>(
    '/api/risk-analysis/assessment-blueprints',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
  return res.data;
}

export async function updateAssessmentBlueprint(
  id: string,
  payload: UpdateAssessmentBlueprintPayload,
): Promise<AssessmentBlueprint> {
  const res = await apiFetch<{ data: AssessmentBlueprint }>(
    `/api/risk-analysis/assessment-blueprints/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
  return res.data;
}

export async function bulkArchiveAssessmentBlueprints(
  assessmentIds: string[],
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/assessment-blueprints/archive',
    { method: 'POST', body: JSON.stringify({ assessmentIds }) },
    API_ROOT_URL,
  );
}

export async function fetchAssessmentBlueprintDetail(
  id: string,
): Promise<AssessmentBlueprintDetail> {
  const res = await apiFetch<
    { data: AssessmentBlueprintDetail } | AssessmentBlueprintDetail
  >(`/api/risk-analysis/assessment-blueprints/${id}`, {}, API_ROOT_URL);
  return 'data' in res &&
    res.data &&
    typeof res.data === 'object' &&
    'id' in res.data
    ? (res as { data: AssessmentBlueprintDetail }).data
    : (res as AssessmentBlueprintDetail);
}

export async function fetchBlueprintCompanies(
  id: string,
  qs: string,
): Promise<BlueprintCompaniesResponse> {
  return apiFetch<BlueprintCompaniesResponse>(
    `/api/risk-analysis/blueprint-companies/${id}?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchBlueprintPrioritization(
  id: string,
  qs: string,
): Promise<BlueprintPrioritizationResponse> {
  return apiFetch<BlueprintPrioritizationResponse>(
    `/api/risk-analysis/blueprint-companies/${id}/prioritization-details?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function recalculateAssessments(
  payload: RecalculateAssessmentsPayload,
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/assessments/calculate',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function exportAssessmentCompanies(
  blueprintId: string,
  companyIds?: string[],
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/exports/assessments',
    {
      method: 'POST',
      body: JSON.stringify({ assessmentBlueprintId: blueprintId, companyIds }),
    },
    API_ROOT_URL,
  );
}

export async function createBulkAssessmentCompanies(
  blueprintId: string,
  companyIds: string[],
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/blueprint-companies/bulk',
    {
      method: 'POST',
      body: JSON.stringify({
        assessmentBlueprintId: blueprintId,
        companyIds: [...new Set(companyIds)],
      }),
    },
    API_ROOT_URL,
  );
}

// ─── Company Assessment Detail ────────────────────────────────────────────────

export async function fetchCompanyAssessment(
  id: string,
): Promise<CompanyAssessment> {
  const res = await apiFetch<{ data: CompanyAssessment }>(
    `/api/risk-analysis/assessments/${id}`,
    {},
    API_ROOT_URL,
  );
  return res.data;
}

export async function fetchAssessmentRiskFactors(
  id: string,
  qs: string,
): Promise<AssessmentRiskFactorsResponse> {
  return apiFetch<AssessmentRiskFactorsResponse>(
    `/api/risk-analysis/assessments/${id}/details?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAssessmentHistory(
  id: string,
  qs: string,
): Promise<AssessmentHistoryResponse> {
  return apiFetch<AssessmentHistoryResponse>(
    `/api/risk-analysis/assessments/${id}/history?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAssessmentRiskPrioritization(
  id: string,
): Promise<AssessmentRiskPrioritizationResponse> {
  return apiFetch<AssessmentRiskPrioritizationResponse>(
    `/api/risk-analysis/assessments/${id}/prioritization-details?count=300&page=0`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAssessmentMitigations(
  assessmentId: string,
  riskId: string,
  page = 0,
  count = 10,
): Promise<AssessmentMitigationsResponse> {
  const qs = new URLSearchParams({
    filter: JSON.stringify({ riskId }),
    page: String(page),
    count: String(count),
  });
  return apiFetch<AssessmentMitigationsResponse>(
    `/api/risk-analysis/mitigations/assessments/${assessmentId}?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAssessmentNotes(
  assessmentId: string,
  riskId: string,
  page = 0,
  count = 20,
): Promise<AssessmentNotesResponse> {
  const qs = new URLSearchParams({
    assessmentId,
    riskId,
    page: String(page),
    count: String(count),
  });
  return apiFetch<AssessmentNotesResponse>(
    `/api/risk-analysis/assessment-notes?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function createAssessmentNote(
  payload: CreateAssessmentNotePayload,
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/assessment-notes',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function deleteAssessmentNote(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/risk-analysis/assessment-notes/${id}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
}

export async function createManualMitigation(
  payload: CreateManualMitigationPayload,
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/mitigations',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function fetchMatchedRules(
  assessmentId: string,
  triggerEvent: string,
  assets: string[],
): Promise<MatchedRulesResponse> {
  const qs = new URLSearchParams({ triggerEvent, assets: assets.join(',') });
  const res = await apiFetch<{ data: MatchedRulesResponse }>(
    `/api/risk-analysis/assessments/${assessmentId}/rules/matched?${qs}`,
    {},
    API_ROOT_URL,
  );
  return res.data;
}

/**
 * Rules behind one risk factor's fulfilled-rules count, for the assessment
 * table's `n/m` cell.
 *
 * Sibling of `fetchMatchedRules`: same response shape, different question. This
 * one asks "which rules produced this risk factor's score", the other "which
 * rules matched this trigger event". The old dashboard's drawer branched
 * between the two endpoints on whether it had a `riskId`.
 */
export async function fetchRiskFulfilledRules(
  assessmentId: string,
  riskId: string,
): Promise<MatchedRulesResponse> {
  const res = await apiFetch<{ data: MatchedRulesResponse }>(
    `/api/risk-analysis/assessments/${assessmentId}/datapoints/${riskId}`,
    {},
    API_ROOT_URL,
  );
  return res.data;
}

export async function fetchAssessmentAllMitigations(
  assessmentId: string,
  page = 0,
  count = 10,
): Promise<AssessmentMitigationsResponse> {
  const qs = new URLSearchParams({ page: String(page), count: String(count) });
  return apiFetch<AssessmentMitigationsResponse>(
    `/api/risk-analysis/mitigations/assessments/${assessmentId}?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAssessmentLevelNotes(
  assessmentId: string,
  page = 0,
  count = 20,
): Promise<AssessmentNotesResponse> {
  const qs = new URLSearchParams({ page: String(page), count: String(count) });
  return apiFetch<AssessmentNotesResponse>(
    `/api/risk-analysis/assessments/${assessmentId}/notes?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchGeoJson(): Promise<GeoJsonObject> {
  const res = await fetch('/countries-map-polygons.json');
  if (!res.ok) throw new Error('Failed to load map polygons');
  return res.json() as Promise<GeoJsonObject>;
}
